import React from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { FormContainer, Input, PopupContainer } from '../../common/components';
import { AUTH_PATTERN, EmailErrors } from '../../auth/forms/validation.pattern';
import { PopupButtons } from '../../common/components/popup';
import { IPopupProps } from './types.type';
import { settingsService } from '../service/settings.service';
import usePopup from '../../common/feature/popup';
import { ConfirmEmail } from './confirm-email.component';
import { useThrottle } from '../../hooks/useThrottle';
import { EmailInputIcon } from '../../common/components/icons/email-input-icon.component';

type Inputs = {
  email: string;
};

export const EditEmail = ({ isOpen, onClose }: IPopupProps) => {
  const { t } = useTranslation();
  const { handleModal } = usePopup();
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors }
  } = useForm<Inputs>();

  const email = useMutation({
    mutationKey: ['changeEmail'],
    mutationFn: async (data: Inputs) =>
      settingsService.changeEmail({ newEmail: data.email }),
    onSuccess: async () => {
      onClose();
      handleModal(ConfirmEmail, { email: watch('email') });
    },
    onError: (err: any) => {
      if (err.response.data.message === 'Email already exists') {
        setError('email', {
          message: EmailErrors.taken,
          type: 'manual'
        });
      }
      if (err.response.data.message === 'Same email') {
        setError('email', {
          message: EmailErrors.same,
          type: 'manual'
        });
      }
    }
  });
  const throttledEmail = useThrottle(email.mutate);

  const onSubmit: SubmitHandler<Inputs> = (data: Inputs) => {
    throttledEmail(data);
  };

  return (
    <PopupContainer caption={t('settings.changeEmail')} isOpen={isOpen} onClose={onClose}>
      <FormContainer onSubmit={handleSubmit(onSubmit)} className="mt-[31px]">
        <Input
          errors={errors}
          name="email"
          label={t('settings.newEmailLabel')}
          placeholder={t('auth.enterHere')}
          type="email"
          icon={<EmailInputIcon />}
          register={register.bind(null, 'email', {
            required: { message: EmailErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.email,
              message: EmailErrors.pattern
            }
          })}
        />

        <PopupButtons onClose={onClose} text={t('settings.updateEmail')} />
      </FormContainer>
    </PopupContainer>
  );
};
